import { useEffect, useRef, useState } from "react";
import {
  Users, FileText, Paperclip, Send, Download, StickyNote, Search,
  ChevronDown, ChevronRight, ClipboardList, FileDown, X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, API } from "@/lib/api";

const FIELD_LABELS = {
  motivo: "Motivo", hallazgos: "Hallazgos", diagnostico: "Diagnóstico",
  plan_tratamiento: "Plan de tratamiento", alergias: "Alergias", receta: "Receta / Rp.",
};

const KIND_LABEL = { consultation: "Consulta", file: "Archivo", note: "Nota" };
const KIND_DOT = { consultation: "bg-[#0284C7]", file: "bg-emerald-500", note: "bg-amber-500" };
function KindIcon({ kind, className = "h-3.5 w-3.5" }) {
  if (kind === "consultation") return <ClipboardList className={className} />;
  if (kind === "file") return <FileText className={className} />;
  return <StickyNote className={className} />;
}

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [data, setData] = useState({}); // patient_id -> {timeline}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { pid, file }
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/doctor/patients").then((r) => setPatients(r.data));
  }, []);

  const loadTimeline = async (pid) => {
    const [cons, recs] = await Promise.all([
      api.get("/consultations", { params: { patient_id: pid } }),
      api.get("/records", { params: { patient_id: pid } }),
    ]);
    const timeline = [
      ...cons.data.map((c) => ({ ...c, _kind: "consultation" })),
      ...recs.data.map((r) => ({ ...r, _kind: r.type })),
    ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    setData((d) => ({ ...d, [pid]: timeline }));
  };

  const toggle = async (pid) => {
    if (openId === pid) { setOpenId(null); return; }
    setOpenId(pid);
    setNote("");
    if (!data[pid]) await loadTimeline(pid);
  };

  const addNote = async (pid) => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post("/records/note", { patient_id: pid, note_text: note });
      setNote("");
      toast.success("Nota agregada");
      await loadTimeline(pid);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onFileSelect = (pid, e) => {
    const file = e.target.files?.[0];
    if (file) setPending({ pid, file });
    if (fileRef.current) fileRef.current.value = "";
  };

  const clearPending = () => setPending(null);

  const uploadPending = async (pid) => {
    if (!pending || pending.pid !== pid) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("patient_id", pid);
      fd.append("note_text", "");
      fd.append("file", pending.file);
      await api.post("/records/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Archivo guardado");
      setPending(null);
      await loadTimeline(pid);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const download = async (rec) => {
    try {
      const res = await api.get(`/files/${rec.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = rec.original_filename || "archivo"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar"); }
  };

  const q = query.trim().toLowerCase();
  const shown = q ? patients.filter((p) => p.patient_name.toLowerCase().includes(q)) : patients;

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Gestión de pacientes</h1>
      <p className="mt-1 text-sm font-semibold text-slate-500">Expediente clínico en línea de tiempo. Expande un paciente para ver su historial.</p>

      <div className="relative mt-5 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar paciente por nombre…" className="rounded-full border-[#E2E8F0] pl-10" data-testid="patient-search-input" />
      </div>

      <div className="mt-6 space-y-3">
        {shown.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#E2E8F0] bg-white p-10 text-center">
            <Users className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-bold text-slate-500">{patients.length === 0 ? "Aún no tienes pacientes." : "Sin coincidencias."}</p>
          </div>
        )}
        {shown.map((p) => {
          const open = openId === p.patient_id;
          const timeline = data[p.patient_id] || [];
          return (
            <div key={p.patient_id} className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white" data-testid={`patient-${p.patient_id}`}>
              <button onClick={() => toggle(p.patient_id)} className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-slate-50" data-testid={`patient-toggle-${p.patient_id}`}>
                <div>
                  <div className="text-sm font-extrabold text-slate-900">{p.patient_name}</div>
                  <div className="text-xs font-bold text-slate-400">{p.visits} visita(s) · última {p.last_visit}</div>
                </div>
                {open ? <ChevronDown className="h-5 w-5 text-[#0284C7]" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
              </button>

              {open && (
                <div className="border-t border-[#E2E8F0] bg-slate-50/50 p-5">
                  <div className="mb-5 rounded-2xl border border-[#E2E8F0] bg-white p-4">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Agregar nota rápida…" className="min-h-16 rounded-xl border-[#E2E8F0]" data-testid={`note-input-${p.patient_id}`} />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button onClick={() => addNote(p.patient_id)} disabled={busy} className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid={`add-note-${p.patient_id}`}><Send className="mr-1.5 h-4 w-4" /> Guardar nota</Button>
                      <input ref={fileRef} type="file" hidden onChange={(e) => onFileSelect(p.patient_id, e)} />
                      <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-full border-[#E2E8F0] font-bold" data-testid={`attach-${p.patient_id}`}><Paperclip className="mr-1.5 h-4 w-4" /> Adjuntar archivo</Button>
                    </div>
                    {pending?.pid === p.patient_id && (
                      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#E2E8F0] bg-slate-50 p-3" data-testid={`pending-file-${p.patient_id}`}>
                        <span className="flex items-center gap-1.5 text-sm font-bold text-slate-700"><FileText className="h-4 w-4 text-emerald-500" /> {pending.file.name}</span>
                        <button type="button" onClick={clearPending} className="rounded-full p-1 text-red-400 hover:bg-red-50" title="Quitar archivo" data-testid={`remove-pending-${p.patient_id}`}><X className="h-4 w-4" /></button>
                        <div className="flex-1" />
                        <Button onClick={() => uploadPending(p.patient_id)} disabled={busy} className="rounded-full bg-emerald-500 font-bold hover:bg-emerald-600" data-testid={`save-file-${p.patient_id}`}><Send className="mr-1.5 h-4 w-4" /> Guardar archivo</Button>
                      </div>
                    )}
                  </div>

                  <div className="relative pl-6">
                    <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#E2E8F0]" />
                    {timeline.length === 0 && <p className="text-sm font-bold text-slate-400">Sin registros clínicos todavía.</p>}
                    {timeline.map((item) => (
                      <div key={item.id} className="relative mb-4" data-testid={`record-${item.id}`}>
                        <div className={`absolute -left-6 top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white ${KIND_DOT[item._kind] || "bg-amber-500"}`} />
                        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
                              <KindIcon kind={item._kind} />
                              {KIND_LABEL[item._kind] || "Nota"}
                              {item._kind === "consultation" && item.status === "draft" && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-600">borrador</span>}
                            </div>
                            <span className="text-xs font-bold text-slate-400">{(item.created_at || "").slice(0, 16).replace("T", " ")}</span>
                          </div>

                          {item._kind === "consultation" ? (
                            <div className="mt-3 space-y-2">
                              {Object.keys(FIELD_LABELS).map((k) => item[k] ? (
                                <div key={k}>
                                  <div className="text-xs font-bold uppercase tracking-wider text-[#0284C7]">{FIELD_LABELS[k]}</div>
                                  <div className="text-sm font-semibold text-slate-700 whitespace-pre-line">{item[k]}</div>
                                </div>
                              ) : null)}
                              {item.receta && (
                                <a href={`${API}/consultations/${item.id}/receta.pdf?v=${encodeURIComponent(item.updated_at || "")}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] px-3 py-1.5 text-xs font-bold text-[#0284C7]" data-testid={`receta-pdf-${item.id}`}>
                                  <FileDown className="h-3.5 w-3.5" /> Receta PDF
                                </a>
                              )}
                            </div>
                          ) : (
                            <>
                              {item.note_text && <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-700">{item.note_text}</p>}
                              {item._kind === "file" && (
                                <button onClick={() => download(item)} className="mt-3 flex items-center gap-1.5 rounded-full border border-[#E2E8F0] px-3 py-1.5 text-xs font-bold text-[#0284C7]" data-testid={`download-${item.id}`}><Download className="h-3.5 w-3.5" /> {item.original_filename}</button>
                              )}
                            </>
                          )}
                          <div className="mt-2 text-xs font-bold text-slate-300">por {item.created_by}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
